import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InputField from '../components/InputField';
import Header from '../others/Header';
import config from '../config/config';
import ConfirmModal from '../others/ConfirmModal';
import { useToast } from '../components/ToastContext';
import {
  FaUser,
  FaEnvelope,
  FaKey,
  FaEdit,
  FaTrash,
  FaBuilding,
  FaUserShield,
  FaUserTie,
  FaUserCircle,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';

function UserRegistration() {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    repetirSenha: '',
    tipoUsuario: '',
    empresa: ''
  });

  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [errors, setErrors] = useState({});
  const { API_BASE_URL } = config;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteConfirmation = (usuario) => {
    setUserToDelete(usuario);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (userToDelete) {
      await handleDelete(userToDelete);
      setIsConfirmModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleUnauthorized = () => {
    window.location.href = "/login";
  };

  const fetchUsuarios = async () => {
    const token = localStorage.getItem("jwtToken");
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/usuarios`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsuarios(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao buscar usuários:', error);
        addToast('error', 'Erro ao carregar usuários. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpresas = async () => {
    const token = localStorage.getItem("jwtToken");

    try {
      const response = await axios.get(`${API_BASE_URL}/empresas`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setEmpresas(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        //handleUnauthorized();
      } else {
        console.error('Erro ao buscar empresas:', error);
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      handleUnauthorized();
    } else {
      const decodedToken = JSON.parse(atob(token.split('.')[1]));
      setUserType(decodedToken.tipoUsuario);
      fetchUsuarios();
      fetchEmpresas();
    }
  }, []);

  const validateFields = () => {
    const newErrors = {};

    if (!formData.nome) newErrors.nome = 'Nome é obrigatório';
    if (!formData.email) newErrors.email = 'Email é obrigatório';
    if (!isEditing && !formData.senha) newErrors.senha = 'Senha é obrigatória';
    if (!isEditing && formData.senha !== formData.repetirSenha) newErrors.repetirSenha = 'As senhas não coincidem';
    if (!formData.tipoUsuario) newErrors.tipoUsuario = 'Tipo de Usuário é obrigatório';
    
    // CORREÇÃO: Managers devem poder selecionar empresa para qualquer tipo de usuário
    if (userType === 'MANAGER' && !formData.empresa) {
      newErrors.empresa = 'Empresa é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;

    // CORREÇÃO: Preparar dados corretamente para a API
    const userData = {
      nome: formData.nome,
      email: formData.email,
      senha: formData.senha,
      tipoUsuario: formData.tipoUsuario
    };

    // CORREÇÃO: Adicionar empresa apenas se for manager ou se o usuário for de tipo específico
    if (userType === 'MANAGER' && formData.empresa) {
      userData.empresa = { idEmpresa: parseInt(formData.empresa) };
    }

    // CORREÇÃO: Para edição, não enviar senha se estiver vazia
    if (isEditing && !formData.senha) {
      delete userData.senha;
    }

    console.log('Enviando dados:', userData); // Para debug

    try {
      const token = localStorage.getItem("jwtToken");
      setLoading(true);
      
      if (isEditing) {
        await axios.put(`${API_BASE_URL}/usuarios/${editUserId}`, userData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setIsEditing(false);
        setEditUserId(null);
        addToast('success', 'Usuário atualizado com sucesso!');
      } else {
        await axios.post(`${API_BASE_URL}/usuarios`, userData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        addToast('success', 'Usuário cadastrado com sucesso!');
      }
      
      await fetchUsuarios();
      resetForm();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao registrar ou editar usuário:', error);
        const errorMessage = error.response?.data?.message || 'Erro ao salvar usuário. Tente novamente.';
        addToast('error', errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      senha: '',
      repetirSenha: '',
      tipoUsuario: '',
      empresa: ''
    });
    setErrors({});
  };

  const handleEdit = (usuario) => {
    setFormData({
      nome: usuario.nome || '',
      email: usuario.email || '',
      senha: '', // Não preencher senha na edição por segurança
      repetirSenha: '',
      tipoUsuario: usuario.tipoUsuario || '',
      empresa: usuario.empresa?.idEmpresa?.toString() || ''
    });

    setIsEditing(true);
    setEditUserId(usuario.idUsuario);
    
    document.getElementById('form-usuario').scrollIntoView({ behavior: 'smooth' });
  };

  const handleDelete = async (usuario) => {
    try {
      const token = localStorage.getItem("jwtToken");
      setLoading(true);
      
      await axios.delete(`${API_BASE_URL}/usuarios/${usuario.idUsuario}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      await fetchUsuarios();
      addToast('success', 'Usuário excluído com sucesso!');
    } catch (error) {
      if (error.response && error.response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Erro ao excluir usuário:', error);
        addToast('error', 'Erro ao excluir usuário. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditUserId(null);
    resetForm();
  };

  // Funções auxiliares
  const formatarTipoUsuario = (tipoUsuario) => {
    const tipos = {
      'GERENTE': 'Gerente',
      'FUNCIONARIO': 'Colaborador',
      'MANAGER': 'Manager'
    };
    return tipos[tipoUsuario] || tipoUsuario;
  };

  const getTipoUsuarioIcon = (tipoUsuario) => {
    const icons = {
      'GERENTE': FaUserShield,
      'FUNCIONARIO': FaUserTie,
      'MANAGER': FaUserCircle
    };
    return icons[tipoUsuario] || FaUser;
  };

  const getTipoUsuarioColor = (tipoUsuario) => {
    const colors = {
      'GERENTE': '#2196F3',
      'FUNCIONARIO': '#4CAF50',
      'MANAGER': '#9C27B0'
    };
    return colors[tipoUsuario] || '#666';
  };

  const getStatusStyle = (ativo) => {
    return ativo ? cardStyles.statusAtivo : cardStyles.statusInativo;
  };

  // Componente Card para cada usuário
  const UsuarioCard = ({ usuario }) => {
    const TipoUsuarioIcon = getTipoUsuarioIcon(usuario.tipoUsuario);
    const tipoUsuarioColor = getTipoUsuarioColor(usuario.tipoUsuario);

    return (
      <div style={cardStyles.card}>
        {/* Header do Card */}
        <div style={cardStyles.cardHeader}>
          <div style={cardStyles.userInfo}>
            <div style={{...cardStyles.userAvatar, backgroundColor: tipoUsuarioColor}}>
              <TipoUsuarioIcon style={cardStyles.userAvatarIcon} />
            </div>
            <div>
              <h3 style={cardStyles.userNome}>{usuario.nome}</h3>
              <p style={cardStyles.userEmail}>{usuario.email}</p>
            </div>
          </div>
          <div style={cardStyles.cardActions}>
            <button
              onClick={() => handleEdit(usuario)}
              style={cardStyles.editButton}
              title="Editar usuário"
            >
              <FaEdit />
            </button>
            <button
              onClick={() => handleDeleteConfirmation(usuario)}
              style={cardStyles.deleteButton}
              title="Excluir usuário"
            >
              <FaTrash />
            </button>
          </div>
        </div>

        {/* Informações do Usuário */}
        <div style={cardStyles.cardBody}>
          <div style={cardStyles.infoGrid}>
            <div style={cardStyles.infoItem}>
              <div style={cardStyles.infoLabel}>
                <FaUserShield style={cardStyles.infoIcon} />
                Tipo de Usuário
              </div>
              <div style={{...cardStyles.tipoUsuario, color: tipoUsuarioColor}}>
                {formatarTipoUsuario(usuario.tipoUsuario)}
              </div>
            </div>

            <div style={cardStyles.infoItem}>
              <div style={cardStyles.infoLabel}>
                <FaBuilding style={cardStyles.infoIcon} />
                Empresa
              </div>
              <span style={cardStyles.infoValue}>
                {usuario.empresa?.nomeFantasia || 'Não vinculada'}
              </span>
            </div>
          </div>

          {/* Status e Informações Adicionais */}
          <div style={cardStyles.additionalInfo}>
            <div style={cardStyles.statusContainer}>
              <div style={cardStyles.statusItem}>
                <span style={cardStyles.statusLabel}>Status:</span>
                <span style={getStatusStyle(usuario.ativo)}>
                  {usuario.ativo ? (
                    <>
                      <FaCheckCircle style={cardStyles.statusIcon} />
                      Ativo
                    </>
                  ) : (
                    <>
                      <FaTimesCircle style={cardStyles.statusIcon} />
                      Inativo
                    </>
                  )}
                </span>
              </div>
              
              {usuario.dataCriacao && (
                <div style={cardStyles.statusItem}>
                  <span style={cardStyles.statusLabel}>Criado em:</span>
                  <span style={cardStyles.infoValue}>
                    {new Date(usuario.dataCriacao).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ID do Usuário */}
        <div style={cardStyles.cardFooter}>
          <span style={cardStyles.userId}>ID: {usuario.idUsuario}</span>
        </div>
      </div>
    );
  };

  const inputFieldStyle = (fieldName) => ({
    ...styles.inputField,
    borderColor: errors[fieldName] ? '#dc3545' : '#e9ecef',
    opacity: loading ? 0.7 : 1
  });

  return (
    <div>
      <Header />
      <div style={styles.container}>
        {/* Formulário */}
        <div id="form-usuario">
          <h2 style={styles.header}>
            {isEditing ? 'Editar Usuário' : 'Cadastro de Usuário'}
          </h2>
          <h4 style={styles.subHeader}>
            Preencha os dados abaixo para {isEditing ? 'editar' : 'cadastrar'} o usuário
          </h4>

          <form onSubmit={handleSubmit} style={styles.form}>
            {/* CORREÇÃO: Campo Empresa sempre visível para MANAGER */}
            {userType === 'MANAGER' && (
              <div style={styles.inputGroup}>
                <div style={styles.fieldContainer}>
                  <label style={styles.label}>
                    <FaBuilding style={styles.labelIcon} />
                    Empresa *
                  </label>
                  <select
                    value={formData.empresa}
                    onChange={(e) => handleInputChange('empresa', e.target.value)}
                    disabled={loading}
                    style={inputFieldStyle('empresa')}
                  >
                    <option value="">Selecione a Empresa</option>
                    {empresas.map(empresa => (
                      <option key={empresa.idEmpresa} value={empresa.idEmpresa}>
                        {empresa.nomeFantasia} - {empresa.cnpj ? empresa.cnpj.substring(0, 14) + '...' : 'Sem CNPJ'}
                      </option>
                    ))}
                  </select>
                  {errors.empresa && <span style={styles.errorText}>{errors.empresa}</span>}
                </div>
              </div>
            )}

            {/* Informações Pessoais */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>
                  <FaUser style={styles.labelIcon} />
                  Nome Completo *
                </label>
                <InputField
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder="Digite o nome completo"
                  disabled={loading}
                  style={inputFieldStyle('nome')}
                />
                {errors.nome && <span style={styles.errorText}>{errors.nome}</span>}
              </div>

              <div style={styles.fieldContainer}>
                <label style={styles.label}>
                  <FaEnvelope style={styles.labelIcon} />
                  E-mail *
                </label>
                <InputField
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="exemplo@email.com"
                  disabled={loading}
                  style={inputFieldStyle('email')}
                />
                {errors.email && <span style={styles.errorText}>{errors.email}</span>}
              </div>
            </div>

            {/* Senhas */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>
                  <FaKey style={styles.labelIcon} />
                  {isEditing ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}
                </label>
                <InputField
                  type="password"
                  value={formData.senha}
                  onChange={(e) => handleInputChange('senha', e.target.value)}
                  placeholder={isEditing ? "Deixe em branco para manter atual" : "Digite a senha"}
                  disabled={loading}
                  style={inputFieldStyle('senha')}
                />
                {errors.senha && <span style={styles.errorText}>{errors.senha}</span>}
              </div>

              {!isEditing && (
                <div style={styles.fieldContainer}>
                  <label style={styles.label}>
                    <FaKey style={styles.labelIcon} />
                    Confirmar Senha *
                  </label>
                  <InputField
                    type="password"
                    value={formData.repetirSenha}
                    onChange={(e) => handleInputChange('repetirSenha', e.target.value)}
                    placeholder="Repita a senha"
                    disabled={loading}
                    style={inputFieldStyle('repetirSenha')}
                  />
                  {errors.repetirSenha && <span style={styles.errorText}>{errors.repetirSenha}</span>}
                </div>
              )}
            </div>

            {/* Tipo de Usuário */}
            <div style={styles.inputGroup}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>
                  <FaUserShield style={styles.labelIcon} />
                  Tipo de Usuário *
                </label>
                <select
                  value={formData.tipoUsuario}
                  onChange={(e) => handleInputChange('tipoUsuario', e.target.value)}
                  disabled={loading}
                  style={inputFieldStyle('tipoUsuario')}
                >
                  <option value="">Selecione o Tipo de Usuário</option>
                  <option value="GERENTE">Gerente</option>
                  <option value="FUNCIONARIO">Colaborador</option>
                  {userType === 'MANAGER' && <option value="MANAGER">Manager</option>}
                </select>
                {errors.tipoUsuario && <span style={styles.errorText}>{errors.tipoUsuario}</span>}
              </div>
            </div>

            {/* Botões */}
            <div style={styles.buttonContainer}>
              <button 
                style={{ 
                  ...styles.button, 
                  backgroundColor: isEditing ? '#2196F3' : '#4CAF50',
                  opacity: loading ? 0.7 : 1
                }} 
                type="submit"
                disabled={loading}
              >
                {loading ? 'Salvando...' : (isEditing ? 'Atualizar Usuário' : 'Cadastrar Usuário')}
              </button>
              
              {isEditing && (
                <button 
                  style={{ 
                    ...styles.button, 
                    backgroundColor: '#f44336', 
                    marginLeft: '10px',
                    opacity: loading ? 0.7 : 1
                  }} 
                  onClick={handleCancelEdit}
                  type="button"
                  disabled={loading}
                >
                  Cancelar Edição
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Cards dos Usuários */}
        <div style={styles.cardsSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.header}>Usuários Cadastrados</h2>
            <div style={styles.cardsInfo}>
              <span style={styles.cardsCount}>{usuarios.length} usuário(s) cadastrado(s)</span>
            </div>
          </div>
          
          {loading ? (
            <div style={styles.loading}>
              <div style={styles.loadingSpinner}></div>
              Carregando usuários...
            </div>
          ) : usuarios.length === 0 ? (
            <div style={styles.emptyState}>
              <FaUser style={styles.emptyIcon} />
              <h3>Nenhum usuário cadastrado</h3>
              <p>Cadastre o primeiro usuário usando o formulário acima</p>
            </div>
          ) : (
            <div style={styles.cardsGrid}>
              {usuarios.map(usuario => (
                <UsuarioCard key={usuario.idUsuario} usuario={usuario} />
              ))}
            </div>
          )}
        </div>

        {/* Modal de Confirmação */}
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsConfirmModalOpen(false)}
          message={`Tem certeza que deseja excluir o usuário "${userToDelete?.nome}"?`}
        />
      </div>
    </div>
  );
}

// Estilos (mantidos iguais)
const cardStyles = {
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '20px',
    marginBottom: '20px',
    border: '1px solid #e9ecef',
    transition: 'all 0.3s ease',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    borderBottom: '2px solid #f8f9fa',
    paddingBottom: '15px',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
  },
  userAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
  },
  userAvatarIcon: {
    fontSize: '20px',
  },
  userNome: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
  },
  userEmail: {
    margin: '5px 0 0 0',
    fontSize: '14px',
    color: '#666',
  },
  cardActions: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '8px 12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  deleteButton: {
    padding: '8px 12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.3s ease',
  },
  cardBody: {
    marginBottom: '15px',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '15px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  infoLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#666',
    fontWeight: '600',
  },
  infoIcon: {
    fontSize: '14px',
    color: '#6f42c1',
  },
  tipoUsuario: {
    fontSize: '14px',
    fontWeight: '600',
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: '#f8f9fa',
  },
  infoValue: {
    fontSize: '14px',
    color: '#333',
    fontWeight: '500',
  },
  additionalInfo: {
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  statusContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusLabel: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '600',
  },
  statusAtivo: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#28a745',
    fontWeight: '600',
    fontSize: '13px',
  },
  statusInativo: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    color: '#dc3545',
    fontWeight: '600',
    fontSize: '13px',
  },
  statusIcon: {
    fontSize: '12px',
  },
  cardFooter: {
    borderTop: '1px solid #e9ecef',
    paddingTop: '12px',
    textAlign: 'center',
  },
  userId: {
    fontSize: '12px',
    color: '#999',
    fontStyle: 'italic',
  },
};

const styles = {
  container: {
    padding: '20px',
    width: '95%',
    maxWidth: '1400px',
    margin: 'auto',
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
  },
  header: {
    color: '#333',
    marginBottom: '10px',
    fontSize: '24px',
    fontWeight: '600',
  },
  subHeader: {
    color: '#666',
    marginBottom: '20px',
    fontSize: '16px',
    fontWeight: 'normal',
  },
  form: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    marginBottom: '30px',
  },
  inputGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginBottom: '25px',
  },
  fieldContainer: {
    flex: '1 1 calc(50% - 20px)',
    minWidth: '280px',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px',
  },
  labelIcon: {
    fontSize: '14px',
    color: '#6f42c1',
  },
  inputField: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #e9ecef',
    borderRadius: '6px',
    backgroundColor: '#fff',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
  },
  errorText: {
    color: '#dc3545',
    fontSize: '12px',
    marginTop: '5px',
    display: 'block',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '15px',
    marginTop: '30px',
    flexWrap: 'wrap',
  },
  button: {
    padding: '12px 25px',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    minWidth: '160px',
  },
  cardsSection: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  cardsInfo: {
    color: '#666',
    fontSize: '14px',
  },
  cardsCount: {
    backgroundColor: '#e7f3ff',
    padding: '8px 15px',
    borderRadius: '20px',
    fontWeight: '600',
    color: '#0066cc',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#666',
    fontSize: '16px',
    gap: '15px',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#dee2e6',
    marginBottom: '15px',
  },
};

export default UserRegistration;